import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { useMemo } from 'react'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useInvoiceActions } from './useInvoiceActions'
import { useInvoiceOptions } from './useInvoiceOptions'
import { usePayments } from './usePayments'
import { useUser } from './useUser'

const usePatientFinanceInfo = props => {
  const { patientId } = props || {}
  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: ['PatientFinanceInfo', patientId],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/summary.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't find the patient finance info")
      }
      return res.json()
    },
    enabled: !!patientId,
  })

  return {
    patientFinanceInfo: data || {},
    isLoading,
    isError,
    refetch,
  }
}

// Puts the cards a renewal returns in place of their old copies in the cached
// card list, and adds the ones it doesn't have yet, such as a restored card
// the list never showed.
const mergePaymentMethods = (paymentMethods, updated) => {
  if (!paymentMethods) return paymentMethods

  const updatedById = new Map(updated.map(method => [method.id, method]))
  const listedIds = new Set(paymentMethods.map(method => method.id))
  return [
    ...paymentMethods.map(method => updatedById.get(method.id) ?? method),
    ...updated.filter(method => !listedIds.has(method.id)),
  ]
}

const usePatientFinancePaymentMethods = props => {
  const queryClient = useQueryClient()
  const { patientId } = props || {}
  const { onOpenSnackbar } = useSnackbarContext()
  const { user } = useUser()
  const isAdmin = user?.admin || false
  const key = ['PatientFinancePaymentMethods', patientId]

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_methods.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't find the patient payment methods")
      }
      return res.json()
    },
    enabled: !!patientId,
  })

  const {
    mutateAsync: deletePaymentMethod,
    isLoading: isDeleteLoading,
    isError: isDeleteError,
  } = useMutation({
    mutationKey: ['DeletePaymentMethod', patientId],
    mutationFn: async ({ paymentMethodId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_methods.json`,
        {
          method: 'DELETE',
          headers,
          body: JSON.stringify({
            payment_method_id: paymentMethodId,
          }),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't delete the payment method")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error deleting payment method')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({
        queryKey: ['PatientFinanceInfo', patientId],
      })
    },
  })

  const creditCards = useMemo(() => {
    const cards =
      data?.filter(paymentMethod => paymentMethod.type === 'credit_card') || []
    if (isAdmin) {
      return cards
    }
    return cards.filter(
      card => card.status === 'active' || card.status === 'expired'
    )
  }, [data, isAdmin])

  const bankAccounts = useMemo(() => {
    return data
      ? data.filter(paymentMethod => paymentMethod.type === 'ach')
      : []
  }, [data])

  return {
    key,
    creditCards,
    bankAccounts,
    paymentMethods: data || [],
    isLoading,
    isError,
    refetch,
    deletePaymentMethod,
    isDeleteLoading,
    isDeleteError,
  }
}

const CARD_RENEWAL_FALLBACK_ERROR =
  "We couldn't update the card expiration. Please try again."

// 403 and 500 come back with an empty body, and the 400 (stale CSRF token)
// and 404 messages are not meant for front desk, so those get our own copy.
const cardRenewalErrorMessage = async res => {
  if (res.status === 400) {
    return 'Your session expired. Reload the page and try again.'
  }
  if (res.status === 403) {
    return "You don't have permission to update cards."
  }
  if (res.status !== 401 && res.status !== 422) {
    return CARD_RENEWAL_FALLBACK_ERROR
  }

  let body = {}
  try {
    body = await res.json()
  } catch {
    body = {}
  }
  return body?.error ?? body?.errors ?? CARD_RENEWAL_FALLBACK_ERROR
}

const usePaymentPlan = props => {
  const { patientId } = props || {}
  const queryClient = useQueryClient()
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    mutateAsync: createPaymentPlan,
    isLoading: isCreatingPaymentPlan,
    isError: isCreatingPaymentPlanError,
  } = useMutation({
    mutationKey: ['CreatePaymentPlan', patientId],
    mutationFn: async variables => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(variables),
        }
      )
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "We couldn't create the payment plan")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error creating payment plan', 'error')
    },
  })

  const {
    mutateAsync: checkDuplicatePaymentPlan,
    isLoading: isCheckingDuplicatePaymentPlan,
    isError: isCheckingDuplicatePaymentPlanError,
  } = useMutation({
    mutationKey: ['CheckDuplicatePaymentPlan'],
    mutationFn: async ({ tokenId, patientId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/duplicate_cards/${tokenId}`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't check for duplicate payment methods")
      }
      const data = await res.json()
      return data.duplicateCard
    },
    onError: () => {
      onOpenSnackbar('Error checking for duplicate payment methods', 'error')
    },
  })

  // Renews the expiration of the saved copy of a reissued card (same number,
  // later expiration), or restores an archived card. The backend reads the new
  // expiration from the Stripe token, so there is no body. Resolves with the
  // updated cards, which also go into the card list until its refetch lands.
  // Errors are left to the caller to display.
  const {
    mutateAsync: renewCardExpiration,
    isLoading: isRenewingCardExpiration,
  } = useMutation({
    mutationKey: ['RenewCardExpiration'],
    mutationFn: async ({ tokenId, patientId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/duplicate_cards/${tokenId}`,
        { method: 'PATCH', headers }
      )
      if (!res.ok) {
        throw new Error(await cardRenewalErrorMessage(res))
      }
      const { paymentMethods } = await res.json()
      return paymentMethods
    },
    onSuccess: (paymentMethods, { patientId }) => {
      queryClient.setQueryData(
        ['PatientFinancePaymentMethods', patientId],
        cached => mergePaymentMethods(cached, paymentMethods)
      )
    },
  })

  return {
    createPaymentPlan,
    isCreatingPaymentPlan,
    isCreatingPaymentPlanError,
    checkDuplicatePaymentPlan,
    isCheckingDuplicatePaymentPlan,
    isCheckingDuplicatePaymentPlanError,
    renewCardExpiration,
    isRenewingCardExpiration,
  }
}

const useStripeClientSecret = () => {
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    mutateAsync: createClientSecret,
    isError: isCreateClientError,
    isLoading: isCreateClientLoading,
  } = useMutation({
    mutationKey: ['StripeClientSecret'],
    mutationFn: async ({ patientId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/stripe_setup_intents.json`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't create the setup intent")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error adding payment method')
    },
  })

  return {
    createClientSecret,
    isCreateClientError,
    isCreateClientLoading,
  }
}

const usePaymentMethods = () => {
  const queryClient = useQueryClient()
  const {
    mutateAsync: updateInvoicesPaymentMethod,
    isLoading: isUpdatingInvoicesPaymentMethod,
    isError: isUpdateInvoicesPaymentMethodError,
  } = useMutation({
    mutationKey: ['UpdateInvoicesPaymentMethod'],
    mutationFn: async ({ patientId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_method.json`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            patient_id: patientId,
          }),
        }
      )
      queryClient.invalidateQueries({
        queryKey: ['PatientFinanceInfo', patientId],
      })
      queryClient.invalidateQueries({
        queryKey: ['PatientFinancePaymentMethods', patientId],
      })
      return res.json()
    },
  })

  const {
    mutateAsync: removeInvoicesPaymentMethod,
    isLoading: isRemovingInvoicesPaymentMethod,
    isError: isRemoveInvoicesPaymentMethodError,
  } = useMutation({
    mutationKey: ['RemoveInvoicesPaymentMethod'],
    mutationFn: async ({ patientId, paymentMethodId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_method.json`,
        {
          method: 'DELETE',
          headers,
          body: JSON.stringify({
            patient_id: patientId,
            payment_method_id: paymentMethodId,
          }),
        }
      )

      return res.json()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['PatientFinanceInfo', variables.patientId],
      })
      queryClient.invalidateQueries({
        queryKey: ['PatientFinancePaymentMethods', variables.patientId],
      })
      queryClient.invalidateQueries({
        queryKey: ['PaymentPlans', variables.patientId],
      })
    },
  })

  return {
    updateInvoicesPaymentMethod,
    isUpdatingInvoicesPaymentMethod,
    isUpdateInvoicesPaymentMethodError,
    removeInvoicesPaymentMethod,
    isRemovingInvoicesPaymentMethod,
    isRemoveInvoicesPaymentMethodError,
  }
}

const usePaymentPlanAdvancePayments = props => {
  const { patientId, paymentPlanId } = props || {}
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    mutateAsync: createAdvancePayment,
    isLoading: isCreatingAdvancePayment,
    isError: isCreatingAdvancePaymentError,
  } = useMutation({
    mutationKey: ['CreateAdvancePayment', patientId, paymentPlanId],
    mutationFn: async variables => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/advance_payment.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(variables),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't create the advance payment")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error creating advance payment', 'error')
    },
  })

  return {
    createAdvancePayment,
    isCreatingAdvancePayment,
    isCreatingAdvancePaymentError,
  }
}

const usePaymentPlanInvoices = props => {
  const { patientId, paymentPlanId } = props || {}
  const { onOpenSnackbar } = useSnackbarContext()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['PaymentPlanInvoices', patientId, paymentPlanId],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't find the payment plan invoices")
      }
      return res.json()
    },
    enabled: !!patientId && !!paymentPlanId,
  })

  const {
    mutateAsync: cancelInvoice,
    isLoading: isCancelingInvoice,
    isError: isCancelInvoiceError,
  } = useMutation({
    mutationKey: ['CancelInvoice', patientId, paymentPlanId],
    mutationFn: async ({ invoiceId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/cancels.json`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't cancel the invoice")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error canceling invoice', 'error')
    },
  })

  const {
    mutateAsync: createInvoicePayment,
    isLoading: isCreatingInvoicePayment,
    isError: isCreateInvoicePaymentError,
  } = useMutation({
    mutationKey: ['CreateInvoicePayment', patientId, paymentPlanId],
    mutationFn: async ({ invoiceId, paymentData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/payments.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(paymentData),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't create the invoice payment")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error creating invoice payment', 'error')
    },
  })

  const {
    mutateAsync: refundInvoicePayment,
    isLoading: isRefundingInvoicePayment,
    isError: isRefundInvoicePaymentError,
  } = useMutation({
    mutationKey: ['RefundInvoicePayment', patientId, paymentPlanId],
    mutationFn: async ({ invoiceId, paymentId, refundData }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/payments/${paymentId}/refunds.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(refundData),
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't refund the invoice payment")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error refunding invoice payment', 'error')
    },
  })

  const {
    mutateAsync: declineInvoicePayment,
    isLoading: isDecliningInvoicePayment,
    isError: isDeclineInvoicePaymentError,
  } = useMutation({
    mutationKey: ['DeclineInvoicePayment', patientId, paymentPlanId],
    mutationFn: async ({ invoiceId, paymentId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/payments/${paymentId}/declines.json`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't decline the invoice payment")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error declining invoice payment', 'error')
    },
  })

  return {
    invoices: data?.invoices || [],
    isLoading,
    isError,
    cancelInvoice,
    isCancelingInvoice,
    isCancelInvoiceError,
    createInvoicePayment,
    isCreatingInvoicePayment,
    isCreateInvoicePaymentError,
    refundInvoicePayment,
    isRefundingInvoicePayment,
    isRefundInvoicePaymentError,
    declineInvoicePayment,
    isDecliningInvoicePayment,
    isDeclineInvoicePaymentError,
  }
}

const usePaymentPlanAwaitingProcesses = props => {
  const { patientId, paymentPlanId } = props || {}
  const { onOpenSnackbar } = useSnackbarContext()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['PaymentPlanAwaitingProcesses', patientId, paymentPlanId],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/awaiting_processes.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't find the awaiting processes")
      }
      return res.json()
    },
    enabled: !!patientId && !!paymentPlanId,
  })

  return {
    awaitingProcesses: data?.awaiting_processes || [],
    isLoading,
    isError,
  }
}

const usePaymentPlanLinks = props => {
  const { patientId, paymentPlanId } = props || {}
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    mutateAsync: createInvoiceLink,
    isLoading: isCreatingInvoiceLink,
    isError: isCreateInvoiceLinkError,
  } = useMutation({
    mutationKey: ['CreateInvoiceLink', patientId, paymentPlanId],
    mutationFn: async ({ invoiceId }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans/${paymentPlanId}/invoices/${invoiceId}/links.json`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't create the invoice link")
      }
      return res.json()
    },
    onError: () => {
      onOpenSnackbar('Error creating invoice link', 'error')
    },
  })

  return {
    createInvoiceLink,
    isCreatingInvoiceLink,
    isCreateInvoiceLinkError,
  }
}

const useLoadPaymentPlans = props => {
  const { patientId } = props || {}

  const { data, isLoading, isError } = useQuery({
    queryKey: ['PaymentPlans', patientId],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/finance/payment_plans`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't find the payment plans")
      }
      return res.json()
    },
    enabled: !!patientId,
  })

  return {
    paymentPlans: data || [],
    isLoading,
    isError,
  }
}

export {
  usePatientFinanceInfo,
  usePatientFinancePaymentMethods,
  usePaymentPlan,
  useStripeClientSecret,
  usePaymentMethods,
  usePaymentPlanAdvancePayments,
  usePaymentPlanInvoices,
  usePaymentPlanAwaitingProcesses,
  usePaymentPlanLinks,
  useInvoiceActions,
  useInvoiceOptions,
  usePayments,
  useLoadPaymentPlans,
}
